/**
 * Scroll Engine - estado agregado de scroll (maxScrollPercent, scrollEvents, scrollVelocity média).
 * Não envia eventos; apenas atualiza estado e notifica o aggregator.
 */

import type { ScrollState } from './types';

const getMaxScrollPercent = (): number => {
  if (typeof window === 'undefined' || typeof document === 'undefined')
    return 0;
  const doc = document.documentElement;
  const bodyScroll = document.body ? document.body.scrollHeight : 0;
  const scrollHeight = Math.max(doc.scrollHeight, bodyScroll);
  const clientHeight = doc.clientHeight;
  if (scrollHeight <= clientHeight) return 100;
  const maxScroll = scrollHeight - clientHeight;
  const current = window.scrollY || window.pageYOffset;
  return Math.min(100, Math.round((current / maxScroll) * 100));
};

let state: ScrollState = {
  maxScrollPercent: 0,
  scrollEvents: 0,
  scrollVelocityAvg: 0,
  lastScrollAt: 0,
};

let scrollListener: (() => void) | null = null;
let onStateChange: ((s: ScrollState) => void) | null = null;
let onScrollEvent: ((s: ScrollState) => void) | null = null;
let lastScrollTop = 0;
let lastScrollTime = 0;
let velocitySum = 0;
const THROTTLE_MS = 100;
const SCROLL_END_DEBOUNCE_MS = 200;
let throttleTimer: ReturnType<typeof setTimeout> | null = null;
let scrollEndTimer: ReturnType<typeof setTimeout> | null = null;

function notify(): void {
  if (onStateChange) onStateChange({ ...state });
}

function updateScroll(): void {
  const now = Date.now();
  const scrollTop =
    typeof window !== 'undefined'
      ? (window.pageYOffset ?? document.documentElement.scrollTop)
      : 0;

  const currentDepth = getMaxScrollPercent();
  state.maxScrollPercent = Math.max(state.maxScrollPercent, currentDepth);
  state.scrollEvents += 1;
  state.lastScrollAt = now;

  if (state.scrollEvents > 1 && lastScrollTime > 0) {
    const dt = (now - lastScrollTime) / 1000;
    const dy = Math.abs(scrollTop - lastScrollTop);
    if (dt > 0) {
      const v = dy / dt;
      velocitySum += v;
      state.scrollVelocityAvg = velocitySum / (state.scrollEvents - 1);
    }
  }

  lastScrollTop = scrollTop;
  lastScrollTime = now;
  notify();

  if (scrollEndTimer !== null) clearTimeout(scrollEndTimer);
  scrollEndTimer = setTimeout(() => {
    scrollEndTimer = null;
    if (onScrollEvent) onScrollEvent(getScrollState());
  }, SCROLL_END_DEBOUNCE_MS);
}

function handleScroll(): void {
  if (throttleTimer !== null) return;
  throttleTimer = setTimeout(() => {
    throttleTimer = null;
    updateScroll();
  }, THROTTLE_MS);
}

export function initScrollBehavior(
  callback?: (s: ScrollState) => void,
  scrollEventCallback?: (s: ScrollState) => void
): void {
  if (typeof window === 'undefined') return;
  onStateChange = callback ?? null;
  onScrollEvent = scrollEventCallback ?? null;
  state = {
    maxScrollPercent: getMaxScrollPercent(),
    scrollEvents: 0,
    scrollVelocityAvg: 0,
    lastScrollAt: Date.now(),
  };
  lastScrollTop = window.pageYOffset ?? document.documentElement.scrollTop;
  lastScrollTime = Date.now();
  velocitySum = 0;

  if (scrollListener) {
    window.removeEventListener('scroll', scrollListener);
  }
  scrollListener = handleScroll;
  window.addEventListener('scroll', scrollListener, { passive: true });
  notify();
}

export function getScrollState(): ScrollState {
  return { ...state };
}

export function resetScrollState(): void {
  state = {
    maxScrollPercent: getMaxScrollPercent(),
    scrollEvents: 0,
    scrollVelocityAvg: 0,
    lastScrollAt: Date.now(),
  };
  lastScrollTop =
    typeof window !== 'undefined'
      ? (window.pageYOffset ?? document.documentElement.scrollTop)
      : 0;
  lastScrollTime = Date.now();
  velocitySum = 0;
  notify();
}

export function destroyScrollBehavior(): void {
  if (scrollListener && typeof window !== 'undefined') {
    window.removeEventListener('scroll', scrollListener);
  }
  scrollListener = null;
  onStateChange = null;
  onScrollEvent = null;
  if (throttleTimer !== null) {
    clearTimeout(throttleTimer);
    throttleTimer = null;
  }
  if (scrollEndTimer !== null) {
    clearTimeout(scrollEndTimer);
    scrollEndTimer = null;
  }
}
